import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { Quote, Plus, X, Phone, Calendar, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const FAQAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-xl border border-[#E8DDCE] overflow-hidden shadow-sm">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full p-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-lg font-bold text-[#123F13]">
              {item.question}
            </h3>
            <div className="text-[#8C2F12] transition-transform duration-300">
              {openIndex === index ? <X size={20} /> : <Plus size={20} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed">
                  {item.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Rygestop = () => {
  const canonicalUrl = "https://corehypnose.dk/behandlinger/rygestop";
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Rygestop med Hypnose og Metakognitiv behandling",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Tingsted 7",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    },
    "description": "Effektivt rygestop i København med hypnose og metakognitiv terapi hos Lars Nissen Corell – Psykoterapeut MPF. Spar penge og forbedr dit helbred. Over 26 års erfaring.",
    "areaServed": "Danmark",
    "serviceType": "Hypnoterapi",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Hvor mange behandlinger skal jeg bruge?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "De fleste af mine klienter bliver røgfri efter 1-2 sessioner. Den første session varer typisk 1,5 time, hvor vi både arbejder med hypnose og de metakognitive værktøjer."
        }
      },
      {
        "@type": "Question",
        "name": "Virker hypnose på alle?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "De fleste mennesker kan hypnotiseres, så længe de er motiverede og villige til at følge instruktionerne."
        }
      },
      {
        "@type": "Question",
        "name": "Tager jeg på i vægt, når jeg stopper?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Med metakognitiv terapi arbejder vi på at bryde selve vane-mønsteret, så du ikke har brug for en erstatning, hvilket minimerer risikoen for vægtøgning."
        }
      }
    ]
  };

  const savingsData = [
    { period: "Om en uge", amount: "kr. 420" },
    { period: "Om en måned", amount: "kr. 1.800" },
    { period: "Om 6 måneder", amount: "kr. 10.800" },
    { period: "Om et år", amount: "kr. 21.600" },
    { period: "Om 5 år", amount: "kr. 108.000" },
    { period: "Om 10 år", amount: "kr. 216.000" }
  ];

  return (
    <ServiceLayout 
      title="Rygestop med Hypnose & Metakognitiv behandling"
      subtitle="Få den indsigt og de værktøjer, der fuldstændigt ændrer din måde at tænke afhængighed og vaner på – så du kan blive røgfri én gang for alle."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/9fc5d2fc-d6f1-415b-8383-a185f306fd1f.png"
      description="Det er en almindelig antagelse, at det er svært at holde op med at ryge. Men med den rette kombination af hypnose og metakognitiv terapi kan det blive den nemmeste og bedste beslutning, du nogensinde har taget."
      benefits={[
        "Ændrer din mentale tilgang til afhængighed",
        "Slip for nikotin-erstatninger som tyggegummi og e-cigaretter",
        "Lær at nyde kaffepausen og sociale lag uden tobak",
        "Forbedret søvn, kondition og mental sundhed",
        "Over 26 års erfaring med vaneændringer"
      ]}
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
    >
      <Helmet>
        <title>Rygestop med Hypnose | Find den bedste Hypnotisør | København</title>
        <meta name="description" content="COREHYPNOSE tilbyder rygestop med Hypnoterapi og Hypnose. Udføres af Certificeret Hypnoterapeut og Hypnotisør, effektivt til rygestop ➜" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Rygestop med Hypnose | Find den bedste Hypnotisør | København" />
        <meta property="og:description" content="COREHYPNOSE tilbyder rygestop med Hypnoterapi og Hypnose. Udføres af Certificeret Hypnoterapeut og Hypnotisør, effektivt til rygestop ➜" />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Hovedtekst sektion */}
      <section className="mb-16">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Rygestop med Hypnose og Metakognitiv behandling i København</h2>
        <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
          <p>
            Det er en helt almindelig antagelse, at det er meget svært og tager rigtig lang tid at holde op med at ryge. Du har sikkert allerede prøvet rigtig mange andre ting som nikotin tyggegummi, e-cigaret, og du er træt af at erstatte nikotin med nikotin.
          </p>
          <p>
            Med metakognitiv behandling og hypnose vil du få den indsigt og forståelse, der fuldstændigt vil ændre din måde at tænke afhængighed og vaner på. Uanset om du ryger, fordi du keder dig, er stresset, eller vil styre din vægt.
          </p>
          <p>
            Den metakognitive tilgang lærer dig at lade tankerne om cigaretter passere uden at give dem opmærksomhed. I stedet for at kæmpe mod trangen, lærer du at lade den være i fred, indtil den forsvinder af sig selv. Kombineret med hypnose, som arbejder med dine ubevidste overbevisninger, skaber vi en kraftfuld synergi, der gør dit rygestop holdbart.
          </p>
          <p>
            Du vil lære at leve et liv uden tobak og stadig kunne nyde en drink, et måltid eller en kaffepause uden at skulle ryge en cigaret. Du kan se mine <Link to="/priser" className="text-[#8C2F12] font-medium underline decoration-[#8C2F12]/30 hover:decoration-[#8C2F12] transition-all">priser her</Link> eller <Link to="/kontakt" className="text-[#8C2F12] font-medium underline decoration-[#8C2F12]/30 hover:decoration-[#8C2F12] transition-all">kontakte mig</Link> for en uforpligtende snak.
          </p>
          <p className="font-medium text-[#123F13] text-xl mt-8">
            Hvad hvis det at holde op med at ryge kan være den nemmeste og bedste ting, du nogensinde har gjort?
          </p>
        </div>
      </section>

      {/* Testimonials (Flyttet op) */}
      <section className="mb-24">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-12 text-center">Det siger mine klienter</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              name: "Henriette O.",
              text: "Jeg er så glad for jeg gik til dig Lars. Jeg kan ikke takke dig nok for at hjælpe mig på det mest vanskelige tidspunkt i mit liv til at blive ikke ryger. Jeg har de varmeste anbefalinger til dig.",
              rating: 5
            },
            {
              name: "Jette H.",
              text: "Jeg har røget i rigtig mange år. Jeg var motiveret og klar til at stoppe og 2 behandlinger var nok til mig. Dejligt at være røgfri. Besparelsen skal bruges til en rejse med mit barnebarn.",
              rating: 5
            },
            {
              name: "Jacob D. Andersen",
              text: "Jeg var meget tilfreds med den hypnose behandling. Jeg fik mit håb om at blive nikotin fri opfyldt, jeg dampede på e-cigaret og har i flere år været på max. nikotin. Jeg er jublende glad over at være IKKE-ryger.",
              rating: 5
            }
          ].map((testimonial, i) => (
            <div key={i} className="bg-white p-8 rounded-xl border border-[#E8DDCE] shadow-sm flex flex-col h-full">
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} className="text-yellow-500">★</span>
                ))}
              </div>
              <p className="text-[#3B352F]/70 italic mb-6 flex-grow">"{testimonial.text}"</p>
              <p className="font-bold text-[#123F13]">— {testimonial.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fradrag sektion (Flyttet op) */}
      <section className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl mb-24">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-serif mb-6">At blive røgfri med hypnose er fradragsberettiget</h2>
          <p className="text-white/80 text-lg leading-relaxed mb-6">
            Hvis du er ansat i en virksomhed, hvor man gerne vil hjælpe sine medarbejdere med at stoppe rygning, så kan din arbejdsgiver trække det fra i skat.
          </p>
          <p className="text-white/80 text-lg leading-relaxed">
            Rygestopkurser, -terapi og -rådgivning er skattefri for medarbejderen, hvis udgiften afholdes som led i en generel personalepolitik for alle virksomhedens medarbejdere.
          </p>
          <div className="mt-8">
            <a 
              href="https://skat.dk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-bold border-b-2 border-white hover:opacity-80 transition-opacity"
            >
              Læs mere på SKATs hjemmeside <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Intro Quote */}
      <section className="text-center max-w-3xl mx-auto mb-16">
        <Quote className="mx-auto text-[#E8DDCE] mb-6" size={48} />
        <p className="text-2xl md:text-3xl font-serif italic text-[#123F13] leading-relaxed">
          “Det eneste mysterium der vil være tilbage er – Hvorfor du blev ved med at ryge i al den tid.”
        </p>
      </section>

      <div className="space-y-24">
        {/* Økonomi sektion */}
        <section>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvor meget vil du spare, når du stopper?</h2>
              <p className="text-[#3B352F]/70 text-lg leading-relaxed mb-6">
                Det er dyrt at ryge. Prisen på cigaretter er steget markant, og en gennemsnitlig ryger bruger tusindvis af kroner hvert år. Herunder kan du se, hvor hurtigt besparelsen løber op, hvis du ryger en pakke om dagen til ca. 60 kr.
              </p>
              <p className="text-[#3B352F]/60 italic text-sm">
                * Baseret på et gennemsnitligt forbrug af 20 cigaretter dagligt til en pris på 60 kr. pr. pakke.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-[#E8DDCE] overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#123F13] text-white">
                    <th className="p-4 font-serif text-lg">RØGFRI</th>
                    <th className="p-4 font-serif text-lg">SPAREDE PENGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8DDCE]">
                  {savingsData.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAF7F2]'}>
                      <td className="p-4 text-[#3B352F] font-medium">{row.period}</td>
                      <td className="p-4 text-[#123F13] font-bold">{row.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at blive røgfri og få dit liv tilbage?</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
              Book din session nu <ArrowRight size={20} />
            </Link>
            <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#123F13] text-[#123F13] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#f3f4f6] transition-all">
              Ring og hør mere <Phone size={20} />
            </a>
          </div>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Rygestop;
